/**
 * Sidecar git helper — manages the single shared git repo at
 * `~/.skaile/sidecars/` (PR-3 § D.5).
 *
 * Each sidecar lives in its own subdirectory under `<rootDir>`. All sidecars
 * share one `.git/` so users can push the entire root to a personal remote
 * (e.g. their own `dotfiles` repo) without juggling N repos.
 *
 * `ensureInitialized()` is idempotent — safe to call on every command. The
 * helper applies a fallback `user.name` / `user.email` only when the global
 * git config is missing those keys, so it never overrides a user's own
 * identity.
 *
 * Mutating commands (`commit()`) are no-ops when `skip: true` is passed; this
 * is how the CLI's `--no-commit` flag flows through.
 *
 * Shells out to the system `git` binary via `child_process` — keeps the
 * dependency surface minimal and matches the rest of the agent-framework's
 * pattern (no `simple-git` / `isomorphic-git`).
 *
 * @docLink packages/library/concepts#sidecar-git
 */

import { spawnSync } from "node:child_process";
import * as fs from "node:fs";
import * as path from "node:path";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/**
 * Result of a raw `git` invocation via {@link SidecarGit.runRaw}.
 *
 * @docLink packages/library/concepts#sidecar-git
 */
export interface GitInvocationResult {
  stdout: string;
  stderr: string;
  exitCode: number;
}

/**
 * Options accepted by {@link SidecarGit.commit}.
 */
export interface SidecarCommitOptions {
  /** Skip the commit entirely. Used by the CLI's `--no-commit` flag. */
  skip?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FALLBACK_USER_NAME = "Skaile CLI";
const FALLBACK_USER_EMAIL = "skaile-cli@local";

const ROOT_README = `# Skaile Sidecar Manifests

This directory is managed by \`skaile source sidecar\` commands. Each
subdirectory is a curated local-overlay manifest for an asset source
registered in your LocalIndex at \`~/.skaile/index.db\`.

You can push this repo to your own remote (e.g. a private dotfiles repo)
to share or back up your sidecars across machines:

\`\`\`bash
skaile source sidecar git -- remote add origin git@example.com:you/sidecars.git
skaile source sidecar git -- push -u origin main
\`\`\`

See the agent-framework docs (\`library/docs/source-sidecars.md\`) for the
full workflow.
`;

const GITATTRIBUTES = "* text=auto eol=lf\n";

// ---------------------------------------------------------------------------
// SidecarGit
// ---------------------------------------------------------------------------

/**
 * Git operations over the shared sidecar root.
 *
 * One instance per CLI invocation is the typical pattern; the helper is
 * stateless beyond the `rootDir`.
 *
 * @docLink packages/library/concepts#sidecar-git
 */
export class SidecarGit {
  constructor(public readonly rootDir: string) {}

  /**
   * Initialize the sidecar root if it doesn't yet exist.
   *
   * Steps (idempotent — each is a no-op when already present):
   *   1. `mkdir -p <rootDir>`
   *   2. `git init -b main <rootDir>` if `<rootDir>/.git` is missing
   *   3. Write `.gitattributes` (`* text=auto eol=lf`) if missing
   *   4. Write `README.md` if missing
   *   5. Apply fallback `user.name` / `user.email` to the repo config if
   *      the global config is missing them
   *   6. Create the seed commit (`chore: initialize sidecar manifests`) if
   *      the repo has no commits yet
   *
   * Subsequent calls don't reset config, so a user who overrode the
   * identity manually keeps their override.
   *
   * @docLink packages/library/concepts#sidecar-git
   */
  async ensureInitialized(): Promise<void> {
    if (!fs.existsSync(this.rootDir)) {
      fs.mkdirSync(this.rootDir, { recursive: true });
    }

    const gitDir = path.join(this.rootDir, ".git");
    const freshRepo = !fs.existsSync(gitDir);
    if (freshRepo) {
      const init = this.spawn(["init", "-b", "main"]);
      if (init.exitCode !== 0) {
        throw new Error(`git init failed: ${init.stderr || init.stdout}`);
      }
    }

    // Seed files (idempotent).
    const attrsPath = path.join(this.rootDir, ".gitattributes");
    if (!fs.existsSync(attrsPath)) fs.writeFileSync(attrsPath, GITATTRIBUTES, "utf-8");
    const readmePath = path.join(this.rootDir, "README.md");
    if (!fs.existsSync(readmePath)) fs.writeFileSync(readmePath, ROOT_README, "utf-8");

    // Apply fallback identity only when missing in global config.
    if (!this.hasGlobalGitConfig("user.name")) {
      this.spawn(["config", "user.name", FALLBACK_USER_NAME]);
    }
    if (!this.hasGlobalGitConfig("user.email")) {
      this.spawn(["config", "user.email", FALLBACK_USER_EMAIL]);
    }

    // Seed commit so subsequent commits have a parent.
    if (freshRepo || !this.hasAnyCommit()) {
      this.spawn(["add", ".gitattributes", "README.md"]);
      this.spawn(["commit", "-m", "chore: initialize sidecar manifests"]);
    }
  }

  /**
   * Stage everything under `<rootDir>/<slug>/` and create a commit.
   *
   * Idempotent: if there are no staged changes after `git add`, the commit
   * is skipped (and resolves successfully — a no-op is fine).
   *
   * @param slug - Sidecar slug (subdirectory name)
   * @param message - Commit message (conventional-commits style preferred;
   *   the CLI uses `feat(<slug>): create sidecar...`, `chore(<slug>): ...`)
   * @param opts - `{ skip: true }` short-circuits without staging or committing
   */
  async commit(slug: string, message: string, opts?: SidecarCommitOptions): Promise<void> {
    if (opts?.skip) return;

    // Stage the slug subtree. If it doesn't exist (e.g. sidecar removed),
    // fall back to `git add -A` so a deletion is captured.
    const slugDir = path.join(this.rootDir, slug);
    if (fs.existsSync(slugDir)) {
      this.spawn(["add", "--", slug]);
    } else {
      this.spawn(["add", "-A"]);
    }

    // Skip commit when nothing is staged (idempotent re-run).
    const status = this.spawn(["status", "--porcelain"]);
    if (status.stdout.trim().length === 0) return;

    const result = this.spawn(["commit", "-m", message]);
    if (result.exitCode !== 0) {
      // Race: `git commit` may report "nothing to commit" if a concurrent
      // command staged + committed. Treat that as a successful no-op.
      const combined = `${result.stdout}\n${result.stderr}`;
      if (/nothing to commit|nothing added to commit/i.test(combined)) return;
      throw new Error(`git commit failed: ${combined.trim()}`);
    }
  }

  /**
   * Check whether the sidecar repo has uncommitted changes.
   *
   * @returns `true` when `git status --porcelain` has any output.
   */
  async hasChanges(): Promise<boolean> {
    const result = this.spawn(["status", "--porcelain"]);
    return result.stdout.trim().length > 0;
  }

  /**
   * Run an arbitrary `git -C <rootDir> <args>` invocation and return the
   * captured stdout/stderr + exit code.
   *
   * Used by `skaile source sidecar git -- <args>` so users don't have to
   * `cd ~/.skaile/sidecars` to operate the repo manually.
   *
   * @param args - Git arguments (no leading `git` token)
   * @returns Combined output + exit code; never throws on non-zero exit
   * @docLink packages/library/concepts#sidecar-git
   */
  async runRaw(args: string[]): Promise<GitInvocationResult> {
    return this.spawn(args);
  }

  // -- Internals -----------------------------------------------------------

  private spawn(args: string[]): GitInvocationResult {
    const result = spawnSync("git", ["-C", this.rootDir, ...args], {
      encoding: "utf-8",
    });
    return {
      stdout: result.stdout ?? "",
      stderr: result.stderr ?? "",
      exitCode: result.status ?? 1,
    };
  }

  private hasGlobalGitConfig(key: string): boolean {
    // `git config --global --get <key>` exits 0 when present, 1 when absent.
    const result = spawnSync("git", ["config", "--global", "--get", key], { encoding: "utf-8" });
    return result.status === 0 && (result.stdout ?? "").trim().length > 0;
  }

  private hasAnyCommit(): boolean {
    const result = this.spawn(["rev-parse", "--verify", "HEAD"]);
    return result.exitCode === 0;
  }
}
